'use client'

import Link from 'next/link'
import s from './HeaderComponent.module.scss'
import { useRef, useState, useLayoutEffect, useEffect } from 'react'
import { motion, AnimatePresence, useMotionValue } from 'framer-motion'
import { usePathname } from 'next/navigation'
import FilterComponent from '../FilterComponent'

type Props = { data: any; categories: any[] }

export default function HeaderComponent({ data, categories }: Props) {
  const pathname = usePathname()
  const isWorkPage = pathname?.replace(/\/+$/, '') === '/work'

  const [isMainMenuOpen, setIsMainMenuOpen] = useState(false)
  const [isFilterOpen, setIsFilterOpen] = useState(false)

  const wrapperRef = useRef<HTMLDivElement>(null)
  const toggleRef = useRef<HTMLButtonElement>(null)
  const navRef = useRef<HTMLElement>(null)

  // Move focus to first link after animation completes; return focus when menu closes
  const focusFirstLink = () => {
    const first = navRef.current?.querySelector<HTMLElement>('a, button')
    first?.focus()
  }

  // Close on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isMainMenuOpen) {
        setIsMainMenuOpen(false)
        toggleRef.current?.focus()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isMainMenuOpen])

  // Arrow key navigation within the open menu
  const handleMenuKeyDown = (e: React.KeyboardEvent) => {
    if (!navRef.current) return
    const items = Array.from(navRef.current.querySelectorAll<HTMLElement>('a, button'))
    const idx = items.indexOf(document.activeElement as HTMLElement)
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      items[(idx + 1) % items.length]?.focus()
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      items[(idx - 1 + items.length) % items.length]?.focus()
    } else if (e.key === 'Home') {
      e.preventDefault()
      items[0]?.focus()
    } else if (e.key === 'End') {
      e.preventDefault()
      items[items.length - 1]?.focus()
    }
  }

  // ✅ posición persistente durante renders
  const x = useMotionValue(0)
  const y = useMotionValue(0)

  // ✅ constraints numéricas (no dependen del container que cambia con el scroll)
  const [constraints, setConstraints] = useState({ top: 0, left: 0, right: 0, bottom: 0 })

  const PADDING = 10 // margen para que no pegue a los bordes

  const computeConstraints = () => {
    const el = wrapperRef.current
    if (!el) return

    const rect = el.getBoundingClientRect()
    const vw = window.innerWidth
    const vh = window.innerHeight // aquí da igual: no lo recalculamos en scroll

    setConstraints({
      left: -(rect.left - PADDING),
      top: -(rect.top - PADDING),
      right: (vw - PADDING) - (rect.right),
      bottom: (vh - PADDING) - (rect.bottom),
    })
  }

  useLayoutEffect(() => {
    computeConstraints()

    // ✅ solo cuando rota/cambia orientación (no en cada scroll)
    const onOrientation = () => {
      // pequeño delay para que el layout se estabilice
      requestAnimationFrame(() => computeConstraints())
    }

    window.addEventListener('orientationchange', onOrientation)
    return () => window.removeEventListener('orientationchange', onOrientation)
  }, [])

  return (
    <div className={s.dragContainer}>
      <motion.div
        ref={wrapperRef}
        className={s.dragWrapper}
        drag
        dragConstraints={constraints}
        dragElastic={0}
        dragMomentum={false}
        style={{ x, y }}
      >
        <header className={s.header} data-site-header>
          <div className={s.logo}>
            <Link href="/"><span translate='no'>Altherr Désile Park</span></Link>

            <button
              ref={toggleRef}
              type="button"
              className={s.arrowContainer}
              aria-expanded={isMainMenuOpen}
              aria-controls="main-nav"
              aria-label={isMainMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              onClick={() => {
                setIsMainMenuOpen(v => !v)
                setIsFilterOpen(false)
              }}
            >
              <svg className={`${s.arrow} ${isMainMenuOpen ? s.arrowOpen : ''}`} width="11" height="7" viewBox="0 0 11 7" aria-hidden="true" focusable="false">
                <path d="M10.5749 0L10.5749 1.725L5.18994 6.525L0 1.695L0 0.03L5.17494 4.83L10.5749 0Z" fill="black" />
              </svg>
            </button>
          </div>

          <AnimatePresence initial={false}>
            {isMainMenuOpen && (
              <motion.nav
                ref={navRef as any}
                id="main-nav"
                aria-label="Main navigation"
                className={s.nav}
                initial={{ height: 0 }}
                animate={{ height: 'auto' }}
                exit={{ height: 0 }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
                style={{ overflow: 'hidden' }}
                onPointerDown={e => e.stopPropagation()}
                onKeyDown={handleMenuKeyDown}
                onAnimationComplete={() => {
                  computeConstraints()
                  focusFirstLink()
                }}
              >
                <ul className={s.menu} role="list">
                  {data?.menu?.links?.map((link: any, i: number) =>
                    link._type === 'linkExternal' ? (
                      <li key={i} className={s.menuItem}>
                        <Link
                          href={link.url}
                          target={link.newWindow ? '_blank' : '_self'}
                          rel="noopener noreferrer"
                          onClick={() => setIsMainMenuOpen(false)}
                        >
                          {link.title}
                        </Link>
                      </li>
                    ) : null
                  )}
                </ul>
              </motion.nav>
            )}
          </AnimatePresence>
        </header>

        {isWorkPage && (
          <FilterComponent
            data={categories}
            isOpen={isFilterOpen}
            onToggle={() => {
              setIsFilterOpen(v => !v)
              setIsMainMenuOpen(false)
              // si abre/cierra, cambia el alto total -> constraints nuevas
              requestAnimationFrame(() => computeConstraints())
            }}
          />
        )}
      </motion.div>
    </div>
  )
}